import React, { Component } from 'react';
import { authService } from '../../../auth/services/authService';
import { AuthenticatedUser } from '../../../auth/models';
import { Button, Select, Loader, Input, Notification, Icon } from '@scuf/common';
import "./GatewayRegistrationCommon.scss";
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { v4 as uuid4 } from 'uuid';
import {
    EUserPersonalisation,
    IUserAccountPreferences,
    IUserProfile,
    IUserSettingsData,
} from './../../../models';
import i18next from './../../../i18next';
import { LanguageCodes } from './../../../locales/LanguageCodeObj';
import logo from '../UnauthenticatedPages/Assets/logo.svg';
import { toast } from 'react-toastify';
import toasts from './../../../modules/commonUtils/toasts';
import { COMMISSIONING_STATUS, GATEWAY_REGISTERED } from '../onboardingUtils';
import { ReactComponent as CloseIcon } from '../../../assets/close.svg';
import { ReactComponent as CorrectIcon } from '../../../assets/correct.svg';
import { useFlags } from 'launchdarkly-react-client-sdk';

interface IMatchParams {
    sysID: string;
    sysType: string;
    sysOwnerCode: string;
    sysPubKey: string;
    sysRegistrState: string;
}
interface IGatewayRegistrationProps extends RouteComponentProps<IMatchParams> {
    flags?: any;
}

interface IOptionOrg {
    value: string,
    text: string
};
interface IGatewayDetails {
    systemId: string;
    systemType: string;
    systemPublicKey: string;
    systemRegistrationState: string;
    systemOwnershipCode: string;
    version: string;
    systemGuid?: string;
};

interface IGatewayRegistrationState {
    user: AuthenticatedUser | null;
    userLock: boolean;
    orgList: Array<IOptionOrg>;
    siteList: Array<IOptionOrg>;
    selectedOrg: string;
    selectedSite: string;
    nodeType: string;
    selectedSiteName: string;
    globalPermissionsLock: boolean;
    languageCode: string;
    enableSubscription: boolean;
    userProfile: IUserProfile | null;
    isLoading: boolean;
    isOrgLoading: boolean;
    isSiteLoading: boolean;
    registerGatewayNotify: boolean;
    gatewayRegistrationRes: string;
    gatewayAssociationRes: string;
    systemGuid: string;
    severity: any;
    gatewayCount: number;
    isDisable: boolean;
    isMultiSite: boolean;
    virtualSite: string;
}

export class GatewayRegistration extends Component<IGatewayRegistrationProps, IGatewayRegistrationState> {

    constructor(props: IGatewayRegistrationProps) {
        super(props);
        this.state = {
            user: null,
            userLock: true,
            orgList: [],
            siteList: [],
            selectedOrg: '',
            selectedSite: '',
            nodeType: '',
            selectedSiteName: '',
            globalPermissionsLock: true,
            languageCode: LanguageCodes.enUS,
            enableSubscription: false,
            userProfile: null,
            isLoading: false,
            isOrgLoading: false,
            isSiteLoading: false,
            registerGatewayNotify: false,
            gatewayRegistrationRes: '',
            gatewayAssociationRes: '',
            systemGuid: '',
            severity: null,
            gatewayCount: 0,
            isDisable: false,
            isMultiSite: false,
            virtualSite: ''
        }
    }

    getSelectedOrgSite = async (systemGuid: string) => {
        const response = await authService.authFetch(
            `${authService.baseURL}/api/GatewayRegistration/systems/${systemGuid}`,
            {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
            }
        );
        return response.json();
    }

    getOrg = async () => {
        this.setState({ isOrgLoading: true });
        const response = await authService.authFetch(
            `${authService.baseURL}/api/RootOrganisations/LCBS?fetchOnPrem=true`,
            {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
            }
        );
        this.setState({ isOrgLoading: false });
        return response.json();
    }

    getVirtualSitesForAnOrg = async (orgId: any) => {
        const response = await authService.authFetch(
            `${authService.baseURL}/api/v1/nodes/LCBS/${orgId}/${orgId}/descendant-nodes/VirtualSite`,
            {
                method: 'GET'
            }
        ).then((response) => {
            if(response.status === 200) {
                return response.json();
            } else if(response.status === 404) {
                return [];
            }
        })
        return response;
    }


    getSites = async (id: any) => {
        this.setState({ isSiteLoading: true });
        const siteParams = this.state.nodeType === 'VirtualSite' ? `VirtualSite` : 'Site';
        const response = await authService.authFetch(
            `${authService.baseURL}/api/v1/organisations/LCBS/${id}/${id}/descendant-nodes/${siteParams}`,
            {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                },
            }
        );
        this.setState({ isSiteLoading: false });
        return response.json();
    }

    mapDataToObject = (data: any) => {
        let tempList: Array<IOptionOrg> = [];
        if (data && data.resources) {            
            data.resources.forEach((item: any) => {
                tempList.push({
                    value: item.id,
                    text: item.name
                })
            })
        }
        return tempList;
    }
    

    componentDidMount = async () => {
        toast.configure();
        authService.getUser().then(async user => {
            if (user !== null) {
                this.getUserAccountSettings();

                const orgDetails = await this.getOrg();
                const OrgList = this.mapDataToObject(orgDetails);

                this.setState({
                    orgList: OrgList.length > 0 ? OrgList : [],
                    selectedOrg: OrgList.length === 1 ? OrgList[0].value : this.state.selectedOrg
                }, () => {
                    if (OrgList.length === 1) {
                        this.siteSelection(OrgList[0].value);
                    }
                });
            } else {
                sessionStorage.setItem('POST_SIGNIN_LOCATION', window.location.href);
                authService.login();
            }
            this.setState({ user: user, userLock: false });
            this.getGlobalPermissions(user);
        });
        
        const gatewayDetails = this.GatewayDetails();
        if (gatewayDetails.systemGuid) { 
            const selectedOrgSite: any = await this.getSelectedOrgSite(gatewayDetails.systemGuid);
            this.setState({ nodeType: selectedOrgSite.nodeType });
            if (selectedOrgSite.orgId && selectedOrgSite.siteId) {
                this.onOrgSelection(selectedOrgSite.orgId);
                this.onSiteSelection(selectedOrgSite.nodeType === 'VirtualSite' ? 'Multiple Site' : selectedOrgSite.siteId);           
                this.setState({
                    gatewayRegistrationRes: i18next.t('success'), 
                    systemGuid: gatewayDetails.systemGuid, 
                    severity : 'success',
                    isDisable: false,
                    registerGatewayNotify: true
                });
            } else {
                toasts.error(i18next.t('invalidSystemGuid'));
            }
        }

        window.onbeforeunload = (e: any) => {
            this.onbeforeunload(e);
        };
    }
    
    onbeforeunload = (e: any) => {
        if (this.state.gatewayRegistrationRes === 'Success' && (this.state.gatewayAssociationRes === '' || this.state.gatewayAssociationRes === i18next.t('failed'))) {
            e.returnValue = '';
            return e.returnValue;
        }
        return;
    }

    getUserAccountSettings = async () => {
        const getAccountSettings = authService.authFetch(
            `${authService.baseURL}/api/user-settings?allDetails=true`,
            {
                method: 'GET'
            }
        );

        const getUserGlobalPrivileges = authService.authFetch(
            `${authService.baseURL}/api/v1/users/me/privileges?scopes=SubscriptionManagement`,
            {
                method: 'GET'
            }
        );

        const getUserProfileDetails = authService.authFetch(
            `${authService.baseURL}/api/v1/users/me`,
            {
                method: 'GET'
            }
        )

        const [accountSettings, userGlobalPrivileges, userProfileDetails] = await Promise.all([
            getAccountSettings,
            getUserGlobalPrivileges,
            getUserProfileDetails
        ]);

        if (accountSettings.status === 200) {
            const userSettings = (await accountSettings.json()) as IUserSettingsData;
            if (userSettings && userSettings.settings.length > 0) {
                let userAccountPreferences: IUserAccountPreferences =
                    userSettings.settings[0].value;
                if (userAccountPreferences !== null && userAccountPreferences) {
                    let lang = userAccountPreferences.prefs.find(
                        elem => elem.name === EUserPersonalisation.language
                    );
                    if (lang && lang.value) {
                        try {
                            let languageCode = lang.value;
                            let translation = require(`./locales/${lang.value}/translations`)
                                .translations[languageCode].translation;
                            i18next.addResources(languageCode, 'translation', translation);
                            i18next.loadResources();
                            i18next.changeLanguage(languageCode);
                            i18next.loadLanguages(languageCode);
                            this.setState({ languageCode: lang.value });
                        } catch (e) {
                            console.error(i18next.t('languageNotSupported'));
                        }
                    }
                }
            }
        } else {
            toasts.error(i18next.t('errorUserSettings'));
        }

        if (userGlobalPrivileges.status === 200) {
            const userPrivileges = await userGlobalPrivileges.json();
            if (
                userPrivileges.length > 0 &&
                userPrivileges
                    .map((e: { name: string }) => e.name)
                    .includes('SubscriptionManagement_Subscriptions_Create')
            ) {
                this.setState({
                    enableSubscription: true
                });
            } else {
                this.setState({
                    enableSubscription: false
                });
            }
        } else {
            toasts.error(i18next.t('errorUserGlobalPrivileges'));
            this.setState({
                enableSubscription: false
            });
        }

        if (userProfileDetails.status === 200) {
            const profileDetails = (await userProfileDetails.json()) as IUserProfile;
            if (profileDetails) {
                this.setState({
                    userProfile: profileDetails
                });
            }
        }
    };

    getGlobalPermissions = (user: AuthenticatedUser | null) => {
        if (user) {
            // Using username as a unique identifier so global perms dont accidentally
            // get mixed up between users if not cleared properly as username is guaranteed unique
            const userName = user.email;

            const globalPermsExistCached = authService.setGlobalPermissionsFromCached(
                userName
            );

            // If there isnt any cached permissions, then block the APP waiting for some
            if (globalPermsExistCached) {
                this.setState({ globalPermissionsLock: false });
            }

            authService
                .setGlobalPermissions(userName)
                .catch(() => {
                    console.error('Couldnt load global permissions');
                    toasts.error('Couldnt load permissions');
                })
                .finally(() => {
                    this.setState({ globalPermissionsLock: false });
                });
        } else {
            this.setState({ globalPermissionsLock: false });
        }
    };
    

    isMultiSiteEnabled = async(org: any, data: any) => {   
        return this.isMultiSiteFeatureEnabled() && this.isValidOrgForMultiSite(data) && !(await this.doesVirtualSiteExist(org)) && data.resources.length > 1;
    }

    isMultiSiteFeatureEnabled(){
        return this.props.flags && this.props.flags.isSingleGateway; 
    }

    isValidOrgForMultiSite = (data: any) => {
        if (data && Array.isArray(data.resources)) {
            for (const resource of data.resources) {
                if (resource && Array.isArray(resource.metadata)) {
                    for (const metadataItem of resource.metadata) {
                        if (metadataItem.name === "SubscriptionDetails" && metadataItem.value) {
                            return true;
                        }
                    }
                }
            }
        }
        return false; 
    }
    
    getVirtualSites = async (orgId: any) => {
        const response = await authService.authFetch(
            `${authService.baseURL}/api/GatewayRegistration/listing/${orgId}`,
            {
                method: 'GET'
            }
        ).then((response) => {
            if(response.status === 200) {
                return response.json();
            } else if(response.status === 404) {
                return [];
            }
        })
        return response;
    }

    doesVirtualSiteExist = async (org: any) => {
        const virtualSites = await this.getVirtualSites(org);        
        if(virtualSites && virtualSites.length > 0) {
            return true;
        }
        return false;        
    }

    updateSiteData = async ( org: any, data: any, allSiteData: any) => {
        const SiteList = this.mapDataToObject(data);
        if(await this.isMultiSiteEnabled(org, data) || this.state.nodeType === 'VirtualSite') {
            SiteList.unshift({ value: 'Multiple Site', text: 'Multiple Site' });
        }
        this.setState({
            siteList: SiteList.length > 0 ? SiteList : [],
            selectedSite: this.state.nodeType === 'VirtualSite' || SiteList.length === 1 ? SiteList[0].value : this.state.selectedSite,
            virtualSite: SiteList.length === 2 ? SiteList[1].text : this.state.virtualSite,
        }, () => {

            const { selectedOrg, isSiteLoading, orgList, siteList } = this.state;
            const organisation = orgList.find(org => org.value === selectedOrg);
            const orgName = organisation && organisation.text;
            if (selectedOrg && !isSiteLoading && siteList.length === 0) {
                toasts.info(i18next.t('siteEmptyMsg', { orgName }));
            }
        });
    }
    siteSelection = async (value: any) => {
        const data = await this.getSites(value);
        const allSiteData = JSON.parse(JSON.stringify(data));
        if (data && data.resources) {
            const sites = data.resources.filter((site: any) => {
                const commStatus = site.metadata.filter((item: any) => item.name === COMMISSIONING_STATUS)[0];
                const gatewayStatus = commStatus && commStatus.value;
                return gatewayStatus !== GATEWAY_REGISTERED;
            });
            data.resources = this.state.systemGuid ? data.resources : sites;
            this.updateSiteData(value, data, allSiteData);
        } else {
            this.setState({
                siteList: []
            });
        }
    }
    
    onOrgSelection = async (value: any) => {
        this.setState({ selectedOrg: value, selectedSite: '' });
        this.siteSelection(value);
    }

    onSiteSelection = (value: any) => {
        if(value === 'Multiple Site') {
            const gatewayDetails = this.GatewayDetails();
            this.setState({ 
                isMultiSite: true,
                virtualSite: gatewayDetails.systemId.split(':')[1]
             });
            
        }else{
            this.setState({
                isMultiSite: false,
            })
        }
        this.setState({
            selectedSite: value
        });
    }

    checkAssociationStatus = (val: any) => {
        return val !== null && val === 'important';
    }
        
    isRegisterDisabled = () => {
        const { orgList, siteList, isOrgLoading, isSiteLoading, selectedSite, virtualSite, isMultiSite } = this.state;
        return orgList.length === 0 || siteList.length === 0 || isOrgLoading || isSiteLoading || selectedSite === '' || (isMultiSite && virtualSite === '')
    }

    GatewayDetails = () => {
        let queryParam = this.props.location.hash.replace("#/?", "");
        let decodedURL = decodeURIComponent(queryParam.replace(/\+/g, " "));
        let queryParamsArray = decodedURL.split("&");
        let gatewayDetails: IGatewayDetails = {
            systemId: '',
            systemType: '',
            systemPublicKey: '',
            systemRegistrationState: '',
            systemOwnershipCode: '',
            version: '',
            systemGuid: ''
        };

        queryParamsArray.forEach((item: string) => {
            if (item.startsWith('systemId')) {
                gatewayDetails.systemId = item.split('=')[1];
            }
            if (item.startsWith('systemType')) {
                gatewayDetails.systemType = item.split('=')[1];
            }
            if (item.startsWith('systemPublicKey')) {
                gatewayDetails.systemPublicKey = item.split(/=(.+)/)[1];
            }
            if (item.startsWith('systemRegistrationState')) {
                gatewayDetails.systemRegistrationState = item.split('=')[1];
            }
            if (item.startsWith('systemOwnershipCode')) {
                gatewayDetails.systemOwnershipCode = item.split('=')[1];
            }
            if (item.startsWith('bajaVersion')) {
                gatewayDetails.version = item.split('=')[1];
            }
            if (item.startsWith('systemGuid')) {
                gatewayDetails.systemGuid = item.split('=')[1];
            }
        });

        return gatewayDetails;
    }

    registerGateway = async () => {
        const { siteList, selectedSite, user, selectedOrg } = this.state;
        let gatewayDetails = this.GatewayDetails();
        let siteName: any = siteList.find((item) => item.value === selectedSite);

        const response = await authService.authFetch(
            `${authService.baseURL}/api/GatewayRegistration/Register/Niagara`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    'SystemType': gatewayDetails.systemType,
                    'country': 'US',
                    'modelId': selectedOrg,
                    'modelTypeId': `${gatewayDetails.systemType}-defaultmodeltype`,
                    'SiteId': this.state.isMultiSite ? this.state.virtualSite : selectedSite,
                    'systemOwnershipCode': gatewayDetails.systemOwnershipCode,
                    'userId': user && user.email,
                    'SystemPublicKey': gatewayDetails.systemPublicKey,
                    'systemId': gatewayDetails.systemId,
                    'SiteName': this.state.isMultiSite ? this.state.virtualSite : siteName.text,
                    'IsMultiSite': this.state.isMultiSite,
                })
            }
        );
        return response;
    }

    associationGateway = async () => {              
        const { siteList, selectedSite, selectedOrg } = this.state;
        let siteName: any = siteList.find((item) => item.value === selectedSite);        
        const virtualSites = await this.getVirtualSitesForAnOrg(this.state.selectedOrg);
        let virtualSiteId: any = '';
        if(virtualSites && virtualSites.resources.length > 0) {
            const virtualSite = virtualSites.resources.filter((site: any) => site.name === this.state.virtualSite);
            if(virtualSite && virtualSite.length > 0) {
                virtualSiteId = virtualSite[0].id;
            }
        }
        if(this.state.isMultiSite && !virtualSiteId) {
            toasts.error("Could not find the virtual site for the selected org. Please contact the administrator.");
            return;
        }

        const response = await authService.authFetch(
            `${authService.baseURL}/api/GatewayRegistration/Association`,
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    'OrgId': selectedOrg,
                    'SiteId': this.state.isMultiSite ? virtualSiteId : selectedSite,
                    'SystemGuid': this.state.systemGuid,
                    'SiteName': this.state.isMultiSite ? this.state.virtualSite : siteName.text,
                    'IsMultiSite': this.state.isMultiSite,
                })
            }
        );
        return response;
    }

    onRegister = async () => {
        this.setState({
            isLoading: true, registerGatewayNotify: false
        });
        
        if(this.state.gatewayRegistrationRes === 'Success') {
            toasts.info(i18next.t('dataloseWarningForAssociation'), false)
            try {
                const response = await this.associationGateway();
                if (response && (response.status === 200 || response.status === 201 || response.status === 204)) {
                    this.setState({
                        gatewayAssociationRes: i18next.t('success'), 
                        systemGuid: this.state.systemGuid, 
                        severity : 'success',
                        isDisable: true
                    });
                } else {
                    this.setState({
                        gatewayAssociationRes: i18next.t('failed'), 
                        registerGatewayNotify: true,
                        severity : 'important',
                        gatewayCount: this.state.gatewayCount + 1
                    });
                }
                toasts.dismiss()
                this.setState({ registerGatewayNotify: true })
            } catch (error) {
                toasts.dismiss();
                this.setState({
                    gatewayAssociationRes: i18next.t('failed'), 
                    registerGatewayNotify: true,
                    severity : 'important',
                    gatewayCount: this.state.gatewayCount + 1
                });
            }
        } else {
            toasts.info(i18next.t('dataloseWarningForRegistration'), false)
            try {
                const response = await this.registerGateway();
                    const res = await response.json()
                    switch (response.status) {
                        case 200:
                        this.setState({
                            gatewayRegistrationRes: i18next.t('success'), 
                            systemGuid: res.SystemGuid, 
                            severity : 'success',
                            isDisable: false
                        });
                        break;
                        case 424:
                        case 500:
                            this.setState({
                                gatewayRegistrationRes: i18next.t('failed'), 
                                severity : 'critical',
                                gatewayCount: this.state.gatewayCount + 1
                            });
                        break;
                    }
                  toasts.dismiss()
                  this.setState({ registerGatewayNotify: true })
            } catch (error) {
                toasts.dismiss();
                toasts.error(i18next.t('gatewayRegistrationFailureMsg'));
                this.setState({
                    gatewayRegistrationRes: i18next.t('failed'), 
                    registerGatewayNotify: true,
                    severity : 'critical',
                    gatewayCount: this.state.gatewayCount + 1
                });
            }
        }
       
        this.setState({
            isLoading: false
        });

    }

    changeLanguage = (languageCode: string) => {
        let translation = require(`../../../locales/${languageCode}/translations`)
            .translations[languageCode].translation;
        i18next.addResources(languageCode, 'translation', translation);
        i18next.loadResources();
        i18next.changeLanguage(languageCode);
        i18next.loadLanguages(languageCode);
        this.setState({ languageCode: languageCode });
    };

    render() {
        const languageOptions = [
            { value: LanguageCodes.enUS, text: 'English US (English-US)' },
            { value: LanguageCodes.enGB, text: 'English GB (English-GB)' },
            { value: LanguageCodes.daDK, text: 'Danish (Dansk)' },
            { value: LanguageCodes.frFR, text: 'French (Français)' },
            { value: LanguageCodes.itIT, text: 'Italian (Italiano)' },
            { value: LanguageCodes.es419, text: 'Spanish-Latin America (Español)' },
            { value: LanguageCodes.deDE, text: 'German (Deutsche)' },
            { value: LanguageCodes.esES, text: 'Spanish-Spain (Español)' },
        ];
        const { selectedOrg, selectedSite, orgList, siteList, isLoading, languageCode, isOrgLoading, isSiteLoading, registerGatewayNotify,
            gatewayRegistrationRes, gatewayAssociationRes, systemGuid, severity, gatewayCount, isDisable, virtualSite } = this.state;
        const gatewayDetails = this.GatewayDetails();
        return (
            <main className="gateway-registration-page-container">
                <section className="headings">
                    <div className="logo-container">
                        <img src={logo} alt="Honeywell" className="honeywell-title" />
                        <span className="vertical-divider" />
                        <div className="smartview-text">{i18next.t('gatewayRegistration')}</div>
                    </div>
                    <div className="user-options">
                        <Select
                            className="language-selector"
                            options={languageOptions}
                            value={languageCode}
                            onChange={value => this.changeLanguage(value)}
                        />
                        <span className="vertical-divider" />
                        <Button type="link"
                            className="logout-text"
                            onClick={() => {
                                authService.signOut();
                            }} >{i18next.t('logout')}</Button>
                    </div>
                </section>
                {isLoading ?
                    (<div className="gateway-loader-wrapper">
                        <Loader text={i18next.t('gatewayRegistrationInProgress', {gateway : this.state.gatewayRegistrationRes === 'Success' ? i18next.t('gatewayAssociation') : i18next.t('gatewayRegistration') })} overlayOpacity={0.5} />
                    </div>) : null}
                <div className="gateway-registration-container">
                    <div className="gateway-registration-body">
                        <div className="additional-label">
                            {i18next.t('reviewGatewayDetails')}
                        </div>
                        <div className="additional-details">
                            <div className="details">
                                <div className="name">{i18next.t('systemId')}</div>
                                <div className="itemValue">{gatewayDetails.systemId}</div>
                            </div>
                            <div className="details">
                                <div className="name">{i18next.t('systemType')} </div>
                                <div className="itemValue">{gatewayDetails.systemType}</div>
                            </div>
                            <div className="details">
                                <div className="name">{i18next.t('Version')} </div>
                                <div className="itemValue">{gatewayDetails.version}</div>
                            </div>
                        </div>
                        <div className="label">
                            {i18next.t('selectSiteToRegsiter')}
                        </div>
                        <div className="select-container">
                            <div className="item">
                                <span className="org-name">{i18next.t('Organisation')}</span>
                                <Select className='org-selector'
                                    placeholder={i18next.t('selectAnOrg')}
                                    value={selectedOrg}
                                    options={orgList}
                                    onChange={(value) => {this.onOrgSelection(value) ; this.setState({ isMultiSite: false })}}
                                    loading={isOrgLoading} 
                                    disabled={this.state.gatewayRegistrationRes === 'Success'} />
                            </div>
                            <div className="item">
                                <span className="site-name">{i18next.t('site')}</span>
                                <Select className='site-selector'
                                    placeholder={i18next.t('selectASite')}
                                    value={selectedSite}
                                    options={siteList}
                                    onChange={(value) => this.onSiteSelection(value)}
                                    loading={isSiteLoading} 
                                    disabled={this.state.gatewayRegistrationRes === 'Success'} />
                            </div>

                            {this.state.isMultiSite &&
                                <div className="item">
                                <span className="virtual-site-name">
                                    Custom Name
                                </span>
                                <Input className='site-selector'
                                    value={virtualSite}
                                    onChange={(value) => this.setState({ virtualSite: value })}
                                    disabled={this.state.nodeType === 'VirtualSite'}
                                     />
                                </div>
                            }
                        </div>
                    </div>
                    <div className="gateway-registration-footer">
                        <Button
                            type="primary"
                            content={this.state.gatewayRegistrationRes === 'Success' ? i18next.t('association') : i18next.t('register')}
                            onClick={(event, data) => this.onRegister()}
                            disabled={this.isRegisterDisabled() || gatewayCount > 3 || isDisable} />
                    </div>
                    {registerGatewayNotify && (
                            <Notification
                            hasIcon={false}
                            className="registration-notification"
                            onCloseClick={() => this.setState({ registerGatewayNotify: false })}
                            severity={severity}
                            >
                                {systemGuid ? (<div className="system-id-label">{i18next.t('systemGuid')}
                                <span className="system-id">{systemGuid}</span></div>) : null}
                                <div className="gateway_status">
                                {gatewayRegistrationRes === i18next.t('failed') ? 
                                    (<CloseIcon className="status-icon"/>) :
                                    (<CorrectIcon className="status-icon"/>) }
                                    <span> {i18next.t('gatewayRegistration')} {gatewayRegistrationRes} </span>
                                </div>
                                {gatewayAssociationRes &&
                                <div className="gateway_status"> 
                                {gatewayAssociationRes === i18next.t('failed') ? 
                                    (<CloseIcon className="status-icon"/>) :
                                    (<CorrectIcon className="status-icon"/>) } 
                                    <span> {i18next.t('gatewayAssociation')} {gatewayAssociationRes} </span>
                                </div>
                                }
                            {severity !== 'success' ?   
                                <div className="contact-admin">{gatewayRegistrationRes === i18next.t('failed') ? i18next.t('gatewayRegistrationFailed') : i18next.t('gatewayAssociationFailed')}</div> : null}
                            {gatewayRegistrationRes === i18next.t('success') && gatewayAssociationRes === '' ?   
                                <div className="do-association-message">{i18next.t('doAssociationMessage')}</div> : null}
                            {gatewayRegistrationRes === i18next.t('success') && this.state.nodeType === null ?  
                                <div className="do-association-message">{i18next.t('errorInAssociation')}</div> : null}
                            </Notification>
                        )}
                </div>
            </main>
        );
    }
}

export default withRouter(GatewayRegistration);


